import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { isNil } from 'lodash-es';
import useChatMessageActions from '../../../../hooks/useChatMessageActions';
import usePushNotifications from '../../../../hooks/usePushNotifications';
import { ProfilePreferenceTypeEnum } from '../../../../proxies/UserProxy';
import { setPushPreference } from '../../../../redux/ducks/StorageDucks';
import { userUpdateProfilePreferenceRequest } from '../../../../redux/ducks/UserDucks';
import type { RootReducerType } from '../../../../redux/reducers/RootReducer.types';
import Button from '../../../button/Button';
import Spinner from '../../../layout/Spinner/Spinner';

type Props = {
  firstName: string;
  lastName: string;
};

const PushEnableTipMessage = ({ firstName, lastName }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const chatModifyModal = useChatMessageActions();
  const { requestPermission, isSupported } = usePushNotifications();
  const { pushPreference } = useSelector(
    (state: RootReducerType) => state.storage,
  );
  const [onPermission, setOnPermission] = useState<boolean>(false);

  const requestPushNotifications = useCallback(() => {
    setOnPermission(true);
    requestPermission();
    dispatch(setPushPreference({ enable: true, updated: Date.now() }));
    dispatch(
      userUpdateProfilePreferenceRequest({
        type: ProfilePreferenceTypeEnum.UserPushNotification,
        value: '1',
      }),
    );
  }, []);

  useEffect(() => {
    if (!isNil(pushPreference)) {
      setOnPermission(false);
    }
  }, [pushPreference]);

  return (
    <span className="flex flex-col text-base">
      <span className="-mt-2 mb-1 font-bold">
        {t('dynamic.common_company_name')}
      </span>
      <span className="mt-2">
        {t('static.chat.push_notification_message_text', {
          name: `${firstName} ${lastName}`,
        })}
      </span>
      <div className="mt-6 flex min-w-full flex-col items-center justify-center gap-2">
        {isSupported ? (
          onPermission ? (
            <span>
              <Spinner color="fill-slate-300" />
            </span>
          ) : !isNil(pushPreference) ? (
            pushPreference.enable ? (
              <span className="-mt-6 min-w-full font-semibold italic text-slate-500">
                {t('static.chat.push_notification_accept_label')}
              </span>
            ) : (
              <span className="-mt-6 min-w-full font-semibold italic text-slate-500">
                {t('static.chat.push_notification_deny_label')}
              </span>
            )
          ) : (
            <>
              <Button
                disabled={false}
                loading={false}
                label={t('static.chat.push_notification_deny_label')}
                onClick={chatModifyModal.denyPushNotifications}
                data-test-id="chat-message-summary-action"
                id="chat-message-summary-action"
                outline
              />
              <Button
                disabled={false}
                loading={false}
                label={t('static.chat.push_notification_accept_label')}
                onClick={requestPushNotifications}
                data-test-id="chat-message-summary-action"
                id="chat-message-summary-action"
                accept
              />
            </>
          )
        ) : (
          <span className="-mt-6 min-w-full font-semibold italic text-slate-500">
            {t('static.chat.push_notification_deny_label')}
          </span>
        )}
      </div>
    </span>
  );
};

export default PushEnableTipMessage;
